/**
 * Pure helpers for the side panel (B7): number formatting, row copy, grouping,
 * keyboard index math and scroll targets. No React, no DOM, no store — every
 * function here is unit-tested in format.test.ts.
 *
 * Copy follows docs/design-spec.md §B.1, §B.2, §F and §H. Minutes are OSRM
 * free-flow values, so the UI always frames them as off-peak (QA-report M2).
 */
import type { Category, CategoryKind, Place } from "@/contracts/data";
import { displayTravel } from "@/contracts/data";
import type { SortKey } from "@/contracts/store";

/* ── numbers ────────────────────────────────────────────────────────── */

/** km: one decimal below 100 ("6.4", "5.0"), whole numbers from 100 ("132"). */
export function formatKm(km: number): string {
  if (!Number.isFinite(km)) return "—";
  if (km >= 99.95) return String(Math.round(km));
  return (Math.round(km * 10) / 10).toFixed(1);
}

/** Minutes split into hours and the remainder (rounded to whole minutes first). */
export function splitMinutes(min: number): { h: number; m: number } {
  const t = Math.max(0, Math.round(min));
  return { h: Math.floor(t / 60), m: t % 60 };
}

/** "48 min" · "1 h 25 min" · "2 h". */
export function formatMinutes(min: number): string {
  if (!Number.isFinite(min)) return "—";
  const { h, m } = splitMinutes(min);
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** One run of a display figure: a number and its small unit ("18" + "min"). */
export interface FigSeg {
  n: string;
  u: string;
}

/** Figure segments for minutes: [18 min] · [1 h][25 min] · [2 h]. */
export function minutesFigure(min: number): FigSeg[] {
  const { h, m } = splitMinutes(min);
  if (!h) return [{ n: String(m), u: "min" }];
  return m ? [{ n: String(h), u: "h" }, { n: String(m), u: "min" }] : [{ n: String(h), u: "h" }];
}

export function kmFigure(km: number): FigSeg[] {
  return [{ n: formatKm(km), u: "km" }];
}

/* ── rows ───────────────────────────────────────────────────────────── */

export type RowFigures =
  | { kind: "travel"; primary: FigSeg[]; secondary: string }
  | { kind: "missing" } //   no route: "—"
  | { kind: "none" }; //     future corridors: the status tag stands in for figures

/**
 * The right-hand figures of a row (§B.1). Sort by distance leads with km;
 * time and A–Z lead with minutes. Future corridors show no figures.
 */
export function rowFigures(p: Pick<Place, "route">, sort: SortKey, kind?: CategoryKind): RowFigures {
  if (kind === "future") return { kind: "none" };
  const t = displayTravel(p);
  if (!t) return { kind: "missing" };
  if (sort === "distance") return { kind: "travel", primary: kmFigure(t.km), secondary: formatMinutes(t.minutes) };
  return { kind: "travel", primary: minutesFigure(t.minutes), secondary: `${formatKm(t.km)} km` };
}

export interface MetaContext {
  kind?: CategoryKind;
  categoryLabel?: string;
  /** Group short name, shown first when the search spans every group. */
  groupShort?: string;
  searching?: boolean;
  /** Road reference of the corridor ("NH753F") for road places. */
  roadRef?: string | null;
  /** The row also carries a status tag on this line ("Upcoming"). */
  status?: boolean;
}

/**
 * The quiet meta line under a row's name (status is rendered separately).
 *  road   → "NH753F · nearest access" or "Nearest access"
 *  other  → locality, else the note (future corridors), else the category label
 *  search → "Social · Viman Nagar"; when the line also carries a status tag the
 *           group gives way ("Lohegaon · UPCOMING"): three parts do not fit a
 *           336 px row, and the status and the place matter more than the group.
 */
export function rowMeta(p: Pick<Place, "locality" | "notes">, ctx: MetaContext): string {
  let base: string;
  if (ctx.kind === "road") base = ctx.roadRef ? `${ctx.roadRef} · nearest access` : "Nearest access";
  else if (p.locality) base = p.locality;
  else if (ctx.kind === "future" && p.notes) base = p.notes;
  else base = ctx.categoryLabel ?? "";
  if (ctx.searching && ctx.groupShort && !(ctx.status && base)) return base ? `${ctx.groupShort} · ${base}` : ctx.groupShort;
  return base;
}

export const NO_ROUTE = "Drive time isn't available for this place yet.";

const STATUS_WORD: Record<string, string> = {
  upcoming: "Upcoming",
  proposed: "Proposed",
  "under-construction": "Under construction",
};

/** Status word for non-existing places ("Upcoming"), else null. */
export function statusWord(status: string): string | null {
  return STATUS_WORD[status] ?? null;
}

/**
 * Accessible name of a list option (§H.2), with the off-peak framing (M2):
 * "Phoenix Marketcity, Viman Nagar. 14 minutes off-peak, 12.9 kilometres by road. Upcoming."
 */
export function optionLabel(p: Pick<Place, "name" | "locality" | "route" | "status">, o: { kind?: CategoryKind; meta?: string; note?: boolean } = {}): string {
  const where = o.meta || p.locality;
  let s = where ? `${p.name}, ${where}.` : `${p.name}.`;
  if (o.kind !== "future") {
    const t = displayTravel(p);
    if (t) {
      const { h, m } = splitMinutes(t.minutes);
      const mins = h ? `${h} ${h === 1 ? "hour" : "hours"}${m ? ` ${m} ${m === 1 ? "minute" : "minutes"}` : ""}` : `${m} ${m === 1 ? "minute" : "minutes"}`;
      s += ` ${mins} off-peak, ${formatKm(t.km)} kilometres by road.`;
    } else {
      s += ` ${NO_ROUTE}`;
    }
  }
  const st = statusWord(p.status);
  if (st) s += ` ${st}.`;
  const note = o.note ? p.route?.note : undefined;
  if (note) s += ` ${sentence(note)}`;
  return s;
}

/** "Within the township" → "Within the township." (keeps an existing full stop). */
export function sentence(s: string): string {
  const t = s.trim();
  return /[.?]$/.test(t) ? t : `${t}.`;
}

/** Native tooltip of a row: the full name, plus the route note when there is one. */
export function rowTitle(p: Pick<Place, "name" | "route">, kind?: CategoryKind): string {
  if (kind !== "future" && !p.route) return `${p.name}. ${NO_ROUTE}`;
  const note = p.route?.note;
  return note ? `${p.name}. ${note}.`.replace(/\.\.$/, ".") : p.name;
}

/* ── copy ───────────────────────────────────────────────────────────── */

/** "1 place" · "22 places". */
export function countLabel(n: number): string {
  return n === 1 ? "1 place" : `${n} places`;
}

/** Live-region text after a search settles (§H.3). */
export function searchAnnouncement(n: number): string {
  if (!n) return "No places match.";
  return n === 1 ? "1 place matches." : `${n} places match.`;
}

/** Group change, from the panel (§H.3): "Social infrastructure. 44 places." */
export function groupAnnouncement(groupLabel: string, n: number): string {
  return `${groupLabel}. ${countLabel(n)}.`;
}

/** Chip name (§H.2): "Education, 22 places". */
export function chipLabel(label: string, n: number): string {
  return `${label}, ${countLabel(n)}`;
}

/** Listbox name (§H.2): "Education, 22 places" · "Search results, 3 places". */
export function listLabel(label: string, n: number): string {
  return `${label}, ${countLabel(n)}`;
}

/** Empty search title (§B.2 / §F empty.title). */
export function emptyTitle(q: string): string {
  return `No places match “${q.trim()}”.`;
}

export const COPY = {
  aria: "Places around Ládera City",
  kicker: "Location and connectivity",
  title: "What's around",
  sub: "Drive times by road from the site, off-peak.",
  offPeak: "Off-peak drive",
  searchPlaceholder: "Search places",
  searchAria: "Search places around Ládera City",
  searchClear: "Clear search",
  searchResults: "Search results",
  emptyDetail: "Try a road, an area or part of a name.",
  sortMenu: "Sort places",
  all: "All",
  about: "About this map",
  attribution: "© OpenStreetMap contributors · Protomaps · © Mapterhorn (Copernicus DEM)",
  groups: "Place groups",
} as const;

export const SORT_BUTTON: Record<SortKey, string> = { time: "By time", distance: "By distance", name: "A to Z" };
export const SORT_ITEM: Record<SortKey, string> = { time: "Nearest by time", distance: "Nearest by distance", name: "A to Z" };
export const SORT_KEYS: readonly SortKey[] = ["time", "distance", "name"];

/* ── grouping ───────────────────────────────────────────────────────── */

export interface Section {
  category: Category | null;
  places: Place[];
  /** Index of the section's first place in the flat list. */
  start: number;
}

/**
 * Splits the already-ordered visible list into category sections. `grouped`
 * is true under the "All" chip without a search (§B: sticky category headers);
 * otherwise one headless section holds everything, in the given order.
 */
export function sectionize(places: Place[], categories: Category[], grouped: boolean): Section[] {
  if (!places.length) return [];
  if (!grouped) return [{ category: null, places, start: 0 }];
  const byId = new Map(categories.map((c) => [c.id, c]));
  const out: Section[] = [];
  places.forEach((p, i) => {
    const last = out[out.length - 1];
    if (last && last.category?.id === p.category) last.places.push(p);
    else out.push({ category: byId.get(p.category) ?? null, places: [p], start: i });
  });
  return out;
}

/* ── keyboard ───────────────────────────────────────────────────────── */

/**
 * Next active index in the list (§B.3): no wrap, Home/End jump, PageUp/Down
 * move by `page`. Returns `i` unchanged for keys it does not handle, and -1
 * for an empty list.
 */
export function listIndex(i: number, key: string, n: number, page = 5): number {
  if (n <= 0) return -1;
  const cur = i < 0 || i >= n ? -1 : i;
  switch (key) {
    case "ArrowDown":
      return cur < 0 ? 0 : Math.min(cur + 1, n - 1);
    case "ArrowUp":
      return cur < 0 ? 0 : Math.max(cur - 1, 0);
    case "Home":
      return 0;
    case "End":
      return n - 1;
    case "PageDown":
      return cur < 0 ? Math.min(page, n - 1) : Math.min(cur + page, n - 1);
    case "PageUp":
      return cur < 0 ? 0 : Math.max(cur - page, 0);
    default:
      return cur;
  }
}

/** Roving focus in the chips toolbar: ←/→ wrap, Home/End jump. null = not handled. */
export function rovingIndex(i: number, key: string, n: number): number | null {
  if (n <= 0) return null;
  const cur = i < 0 || i >= n ? 0 : i;
  switch (key) {
    case "ArrowRight":
      return (cur + 1) % n;
    case "ArrowLeft":
      return (cur - 1 + n) % n;
    case "Home":
      return 0;
    case "End":
      return n - 1;
    default:
      return null;
  }
}

/** Keys 1–4 → group index 0–3 (§B.3), else null. */
export function groupIndexForKey(key: string): number | null {
  return key === "1" || key === "2" || key === "3" || key === "4" ? Number(key) - 1 : null;
}

/* ── scrolling ──────────────────────────────────────────────────────── */

/**
 * The scroll offset that brings [start, start + size) inside the viewport
 * [scroll + padStart, scroll + view − padEnd] with the least movement, or
 * null when it is already inside. Works for both axes.
 */
export function scrollTarget(start: number, size: number, scroll: number, view: number, padStart = 0, padEnd = 0, max = Number.POSITIVE_INFINITY): number | null {
  const top = scroll + padStart;
  const bottom = scroll + view - padEnd;
  let next: number | null = null;
  if (start < top) next = start - padStart;
  else if (start + size > bottom) next = start + size + padEnd - view;
  if (next === null) return null;
  next = Math.max(0, Math.min(next, max));
  return Math.abs(next - scroll) < 0.5 ? null : next;
}

/** Which edges of a horizontal scroller can still scroll (for the fade mask). */
export function scrollEdges(scrollLeft: number, clientWidth: number, scrollWidth: number): { start: boolean; end: boolean } {
  return { start: scrollLeft > 1, end: scrollLeft + clientWidth < scrollWidth - 1 };
}

/** True for inputs, textareas, selects and editable content: global shortcuts must not fire there. */
export function isTextTarget(t: { tagName?: string; isContentEditable?: boolean; type?: string } | null | undefined): boolean {
  if (!t || !t.tagName) return false;
  const tag = t.tagName.toUpperCase();
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (t.type || "text").toLowerCase();
    return !["button", "checkbox", "radio", "range", "reset", "submit", "color", "file", "image"].includes(type);
  }
  return !!t.isContentEditable;
}

/** Stagger index for a row's arrival: the first 10 rows 40 ms apart, the rest together (§E.9). */
export function staggerIndex(i: number): number {
  return Math.min(Math.max(0, i), 10);
}

/**
 * The chip under a horizontal position, for the transparent hit extension
 * above and below the chips row (§B mobile, §A.5 kiosk): a tap in the row's
 * padding presses the chip whose span contains x. -1 when x falls in a gap.
 */
export function chipAtX(spans: ReadonlyArray<{ left: number; right: number }>, x: number): number {
  for (let i = 0; i < spans.length; i++) {
    const s = spans[i];
    if (x >= s.left && x <= s.right) return i;
  }
  return -1;
}

/**
 * What the list is showing, for the row choreography (§E.9): a new key means
 * the rows leave (168 leave) and the new ones arrive staggered. Typing in the
 * search field only changes the "search" part, which the list treats as a
 * quiet swap, never a stagger per keystroke.
 */
export function listKey(s: { group: string; category: string | null; sort: SortKey; searching: boolean }): string {
  return s.searching ? `search|${s.sort}` : `${s.group}|${s.category ?? "*"}|${s.sort}`;
}
